import styled, { type RuleSet } from 'styled-components';
import useContainerQuery from 'hooks/responsive/useContainerQuery';
import { breakpoint, sizes } from 'variables';
import ScrollSnapSliderContext from '../context/ScrollSnapSliderContext';
import useScrollSnapSlider from '../hooks/useScrollSnapSlider';
import ScrollSnapSlider from '../ScrollSnapSlider';
import { RootContainer } from '../shared/styles';
import NavigationFadeButton, { type INavigationButton } from './NavigationFadeButton';

const StyledScrollSnapSlider = styled(ScrollSnapSlider)`
    scroll-snap-type: none;
    scroll-snap-stop: normal;
    gap: 8px;

    ${breakpoint.up(sizes.xl)} {
        scroll-snap-type: x mandatory;
        scroll-snap-stop: always;
    }

    &::-webkit-scrollbar {
        display: none;
    }
`;

const options = {
    draggable: true,
    loop: false
};

type Props<T> = {
    items: T[];
    itemRenderer: (item: T, index: number) => JSX.Element | JSX.Element[];
    arrowRenderer?: (props: INavigationButton) => JSX.Element;
    hideCarouselButtons?: boolean;
    scrollContainerCss?: RuleSet;
    carouselButtonsIndent?: string;
};

function defaultArrowRenderer(props: INavigationButton) {
    const { indent, slideAmount, direction, size } = props;

    return (
        <NavigationFadeButton direction={direction} size={size} withBorder indent={indent} slideAmount={slideAmount} />
    );
}

export default function ItemCarousel<T>({
    items,
    itemRenderer,
    arrowRenderer = defaultArrowRenderer,
    hideCarouselButtons = false,
    scrollContainerCss,
    carouselButtonsIndent,
    ...restProps
}: Props<T>) {
    const { ref, slider } = useScrollSnapSlider(options);

    const [containerRef, slideAmount] = useContainerQuery<number>(
        { '0': 1, '544': 2, '768': 3, '992': 4, '1600': 5 },
        1
    );

    return (
        <ScrollSnapSliderContext.Provider value={slider}>
            <RootContainer {...restProps} ref={containerRef}>
                {!hideCarouselButtons && (
                    <>
                        {arrowRenderer({ direction: 'prev', slideAmount, indent: carouselButtonsIndent })}
                        {arrowRenderer({ direction: 'next', slideAmount, indent: carouselButtonsIndent })}
                    </>
                )}

                <StyledScrollSnapSlider css={scrollContainerCss} ref={ref}>
                    {items.map((item, index) => itemRenderer(item, index))}
                </StyledScrollSnapSlider>
            </RootContainer>
        </ScrollSnapSliderContext.Provider>
    );
}
